import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "./client.ts";
import { closeGate } from "./parentGate.ts";
import type {
  ArchivedChild,
  Badge,
  ChildReport,
  Overview,
  ChildState,
  CurriculumIndex,
  DailyInfo,
  Leaderboard,
  Me,
  Pack,
  QuestsInfo,
  ReviewInfo,
  Shop,
  CurriculumInfo,
} from "./types.ts";

export const keys = {
  me: ["me"] as const,
  state: ["state"] as const,
  curriculum: (id: string) => ["curriculum", id] as const,
  lesson: (id: string) => ["lesson", id] as const,
  review: ["review"] as const,
  daily: ["daily"] as const,
  badges: ["badges"] as const,
  shop: ["shop"] as const,
  quests: ["quests"] as const,
  leaderboard: (board: string, period: string) => ["leaderboard", board, period] as const,
  archived: ["children", "archived"] as const,
  devices: ["devices"] as const,
  overview: ["reports", "overview"] as const,
  report: (childId: string, days: number) => ["reports", childId, days] as const,
};

/** Today's quests with progress (SPEC 7.7, C15); fresh on every visit to the home screen. */
export function useQuests() {
  return useQuery({
    queryKey: keys.quests,
    queryFn: () => api.get<QuestsInfo>("/me/quests"),
    staleTime: 0,
  });
}

/** null when logged out. */
export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: async () => {
      try {
        return await api.get<Me>("/auth/me");
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
  });
}

export function useChildState(enabled = true) {
  return useQuery({
    queryKey: keys.state,
    queryFn: () => api.get<ChildState>("/me/state"),
    enabled,
    staleTime: 30_000,
  });
}

export function useCurriculum(id: string | undefined) {
  return useQuery({
    queryKey: keys.curriculum(id ?? ""),
    queryFn: () => api.get<CurriculumIndex>(`/content/curricula/${id}`),
    enabled: !!id,
    staleTime: 10 * 60_000,
  });
}

export function useLesson(id: string | undefined) {
  return useQuery({ ...lessonQuery(id ?? ""), enabled: !!id });
}

const lessonQuery = (id: string) => ({
  queryKey: keys.lesson(id),
  queryFn: () => api.get<Pack>(`/content/lessons/${id}`),
  staleTime: 10 * 60_000,
});

/** Several packs at once (mixed review/daily sessions). */
export function useLessons(ids: readonly string[]) {
  return useQueries({
    queries: ids.map(lessonQuery),
    combine: (rs) => ({
      data: rs.every((r) => r.data) ? rs.map((r) => r.data as Pack) : undefined,
      isPending: rs.some((r) => r.isPending),
      isError: rs.some((r) => r.isError),
      refetch: () => rs.forEach((r) => void r.refetch()),
    }),
  });
}

export function useReview(enabled = true) {
  return useQuery({
    queryKey: keys.review,
    queryFn: () => api.get<ReviewInfo>("/me/review"),
    enabled,
    staleTime: 0,
  });
}

export function useDaily(enabled = true) {
  return useQuery({
    queryKey: keys.daily,
    queryFn: () => api.get<DailyInfo>("/me/daily"),
    enabled,
    staleTime: 0,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { username: string; password: string }) => api.post<Me>("/auth/login", v),
    onSuccess: (me) => {
      qc.setQueryData(keys.me, me);
      qc.removeQueries({ queryKey: keys.state });
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post("/auth/logout"),
    onSettled: () => {
      qc.setQueryData(keys.me, null);
      qc.removeQueries({ queryKey: keys.state });
    },
  });
}

/** C3: open a child's profile; `pin` when the child has a picture PIN, `parentHelp` = "Bố mẹ mở giúp". */
export function useSelectChild() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: string | { childId: string; pin?: string[]; parentHelp?: boolean }) => {
      const { childId, ...body } = typeof v === "string" ? { childId: v } : v;
      return api.post<ChildState>(`/children/${childId}/select`, body);
    },
    onSuccess: (state) => {
      // the api closed the parent gate: the device is the child's now (T3.9c)
      closeGate();
      qc.setQueryData(keys.state, state);
      qc.setQueryData<Me | null>(keys.me, (me) =>
        me ? { ...me, activeChildId: state.child.id } : me,
      );
    },
  });
}

export function useDeselectChild() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post("/children/deselect"),
    onSuccess: () => {
      qc.removeQueries({ queryKey: keys.state });
      qc.setQueryData<Me | null>(keys.me, (me) => (me ? { ...me, activeChildId: null } : me));
    },
  });
}

/** Parent settings of one child (SPEC 6.2 free unlock, 9.6 sound); refreshes the child list and state. */
export function useChildSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      childId,
      ...settings
    }: {
      childId: string;
      freeUnlock?: boolean;
      sound?: boolean;
      grade?: number;
      notebookLevel?: 1 | 2 | 3 | null;
      leaderboardOptOut?: boolean;
    }) =>
      api.patch<{ freeUnlock: boolean; sound: boolean }>(`/children/${childId}/settings`, settings),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.me });
      void qc.invalidateQueries({ queryKey: keys.state });
    },
  });
}

/** P6 child profiles (SPEC 2.3, T3.4b): every change refreshes the child list. */
function useProfileMutation<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.me });
      void qc.invalidateQueries({ queryKey: keys.archived });
    },
  });
}

export const useAddChild = () =>
  useProfileMutation((v: { displayName: string; color: string; starter: string | null }) =>
    api.post<{ id: string }>("/children", v),
  );
export const useEditChild = () =>
  useProfileMutation(
    ({ childId, ...v }: { childId: string; displayName?: string; color?: string }) =>
      api.patch<{ id: string }>(`/children/${childId}`, v),
  );
export const useArchiveChild = () =>
  useProfileMutation((childId: string) => api.del<{ ok: true }>(`/children/${childId}`));
export const useRestoreChild = () =>
  useProfileMutation((childId: string) => api.post<{ ok: true }>(`/children/${childId}/restore`));
export const useSetPin = () =>
  useProfileMutation(({ childId, pin }: { childId: string; pin: string[] | null }) =>
    pin
      ? api.put<{ hasPin: boolean }>(`/children/${childId}/pin`, { pin })
      : api.del<{ hasPin: boolean }>(`/children/${childId}/pin`),
  );

export function useArchivedChildren() {
  return useQuery({
    queryKey: keys.archived,
    queryFn: () => api.get<{ children: ArchivedChild[] }>("/children/archived"),
  });
}

/** P1 – a tile per child (T3.9); fresh on every visit. */
export function useOverview() {
  return useQuery({
    queryKey: keys.overview,
    queryFn: () => api.get<Overview>("/reports/overview"),
    staleTime: 0,
  });
}

/** P2 – one child's progress over 7 or 30 days (T3.9). */
export function useChildReport(childId: string | undefined, days: 7 | 30) {
  return useQuery({
    queryKey: keys.report(childId ?? "", days),
    queryFn: () => api.get<ChildReport>(`/reports/children/${childId}?days=${days}`),
    enabled: !!childId,
    placeholderData: (prev) => prev,
  });
}

/** P7 – the parent's signed-in devices and password (SPEC 2.2, T3.3c). */
export interface SignedInDevice {
  id: string;
  device: string;
  current: boolean;
  lastSeenAt: string;
}

export function useDevices() {
  return useQuery({
    queryKey: keys.devices,
    queryFn: () =>
      api.get<{ sessions: SignedInDevice[] }>("/auth/sessions").then((r) => r.sessions),
  });
}

function useDeviceMutation<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.devices }),
  });
}

export const useSignOutDevice = () =>
  useDeviceMutation((id: string) => api.del<{ ok: true }>(`/auth/sessions/${id}`));
export const useSignOutOthers = () =>
  useDeviceMutation(() => api.post<{ signedOut: number }>("/auth/logout-all"));
export const useChangePassword = () =>
  useDeviceMutation((v: { current: string; next: string }) =>
    api.post<{ signedOut: number }>("/auth/password", v),
  );

/** C17 – one leaderboard (SPEC 7.10); `board` undefined = the child's first board. */
export function useLeaderboard(board: string | undefined, period: "week" | "month") {
  return useQuery({
    queryKey: keys.leaderboard(board ?? "", period),
    queryFn: () => {
      const q = new URLSearchParams({ period, ...(board ? { board } : {}) });
      return api.get<Leaderboard>(`/leaderboards?${q.toString()}`);
    },
    staleTime: 0,
  });
}

/** The home card told the weekly prize news (D44): don't tell it again. */
export function usePrizesSeen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ ok: true }>("/me/leaderboard-prizes/seen"),
    onSettled: () => void qc.invalidateQueries({ queryKey: keys.state }),
  });
}

/** Every badge with the child's progress (SPEC 7.8, C16). */
export function useBadges() {
  return useQuery({
    queryKey: keys.badges,
    queryFn: () => api.get<{ badges: Badge[] }>("/me/badges").then((r) => r.badges),
  });
}

/** The shop with what the active child owns and wears (SPEC 7.5, C18). */
export function useShop() {
  return useQuery({ queryKey: keys.shop, queryFn: () => api.get<Shop>("/shop") });
}

/** Buy (and put on) an item, or change clothes; the header, avatars and bees follow. */
export function useShopAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      a: { kind: "buy"; itemKey: string } | { kind: "wear"; slot: string; itemKey: string | null },
    ) =>
      a.kind === "buy"
        ? api.post<unknown>("/shop/purchase", { itemKey: a.itemKey })
        : api.post<unknown>("/shop/equip", { slot: a.slot, itemKey: a.itemKey }),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: keys.shop }),
        qc.invalidateQueries({ queryKey: keys.badges }),
        qc.invalidateQueries({ queryKey: keys.me }),
        qc.invalidateQueries({ queryKey: keys.state }),
      ]),
  });
}

/** Grades with content, for the parent's grade picker (T1.9). */
export function useCurricula() {
  return useQuery({
    queryKey: ["curricula"] as const,
    queryFn: () => api.get<{ curricula: CurriculumInfo[] }>("/curricula").then((r) => r.curricula),
    staleTime: 10 * 60_000,
  });
}
